import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { BEATS, SW_BEATS } from "../lib/beats";
import { IMAGES } from "../lib/tokens";
import { useWorld } from "../state/World";

/**
 * The walk-in tease.
 *
 * Founder's idea from his Design revision: a Myst-style click-through of a
 * Place's Spaces, sitting below the fold on the landing page. It proves the
 * product before the visitor has committed to anything — and it costs nothing
 * to run, because it is the same beats array the real walk-in uses.
 *
 * "Click through it — the floor answers back."
 */
const PLACES = [
  {
    key: "arcade",
    clock: "10 o'clock",
    name: "The Arcade",
    tagline: "Where pursuit arrives as play.",
    body: "Stained glass, lamplight, and a hundred small futures you can try at low stakes. Play a pursuit forward before you commit a year to it — then keep the ones that light up.",
    practice: "PlayForward",
    artifact: "A pursuit worth staging",
    beats: BEATS,
    to: "/playforward",
    portal: "playforward",
  },
  {
    key: "hall",
    clock: "7 o'clock",
    name: "The Hall",
    tagline: "Where the how gets worked out.",
    body: "Marble, gilt and two staircases, and a coat closet with a blank wall in it. Put down everything you are carrying, then watch it get gathered, ordered and dated.",
    practice: "Stageward",
    artifact: "A Stage you could act on today",
    beats: SW_BEATS,
    to: "/stageward/walk",
    portal: "stageward",
  },
];

const Walk = ({ place }) => {
  const [i, setI] = useState(0);
  const beat = place.beats[i];
  const total = place.beats.length;

  return (
    <div data-testid={`tease-walk-${place.key}`}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="tracked text-[9px] text-[var(--stage-gold)]/80">
            Inside · the Spaces of {place.name}
          </div>
          <div className="ink-mid mt-1 text-[12px] italic">
            Click through it — the floor answers back
          </div>
        </div>
        <div className="mono text-[10px] text-[var(--stage-gold)]/70">
          Step {String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </div>
      </div>

      <button
        type="button"
        data-testid={`tease-step-${place.key}`}
        onClick={() => setI((v) => (v + 1) % total)}
        className="group relative block aspect-[16/9] w-full overflow-hidden rounded-xl border border-[var(--stage-gold)]/20"
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={beat.key}
            src={IMAGES[beat.image]}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
          />
        </AnimatePresence>

        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-2/3"
          style={{
            background:
              "linear-gradient(to top, rgba(12,7,4,0.94) 0%, rgba(12,7,4,0.6) 45%, rgba(12,7,4,0) 100%)",
          }}
        />
        <span className="absolute inset-x-0 bottom-0 p-4 text-left md:p-6">
          <span className="tracked block text-[8px] text-[var(--stage-gold)]">
            {beat.kicker}
          </span>
          <span className="serif mt-1.5 block text-[15px] leading-snug text-[#f4ece0] md:text-[19px]">
            {beat.line}
          </span>
        </span>

        <span className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 backdrop-blur-sm transition-colors group-hover:bg-black/75">
          <span className="mono text-[8px] uppercase tracking-[0.14em] text-white/70">
            Myst-style walk-in
          </span>
          <ChevronRight size={11} className="text-[var(--stage-gold)]" />
        </span>
      </button>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setI((v) => (v - 1 + total) % total)}
          className="ink-mid rounded-full border border-[var(--stage-gold)]/20 p-1.5 transition-colors hover:border-[var(--stage-gold)]/60"
          aria-label="back a step"
        >
          <ChevronLeft size={12} />
        </button>
        <div className="flex flex-1 gap-1">
          {place.beats.map((b, n) => (
            <button
              key={b.key}
              type="button"
              onClick={() => setI(n)}
              aria-label={b.kicker}
              className="h-[3px] flex-1 rounded-full transition-colors duration-300"
              style={{
                background:
                  n === i ? "var(--stage-gold)" : "rgba(220,181,82,0.22)",
              }}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => setI((v) => (v + 1) % total)}
          className="ink-mid rounded-full border border-[var(--stage-gold)]/20 p-1.5 transition-colors hover:border-[var(--stage-gold)]/60"
          aria-label="on a step"
        >
          <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
};

export const PlaceTease = () => {
  const nav = useNavigate();
  const { cross } = useWorld();

  // Same rule as the map doors: entering a Place is a crossing, not a route.
  const enter = async (p) => {
    const { external } = await cross(p.portal, p.to);
    if (!external) nav(p.to);
  };

  return (
    <div className="space-y-14" data-testid="place-teases">
      {PLACES.map((p) => (
        <div key={p.key} className="parchment rounded-2xl p-6 md:p-9" data-testid={`place-${p.key}`}>
          <div className="grid gap-8 lg:grid-cols-[1fr_1.15fr]">
            <div>
              <div className="tracked mb-3 flex items-center gap-2.5 text-[9px]">
                <span className="rounded-full bg-[var(--stage-gold)] px-2.5 py-1 text-[#1a1206]">
                  Open
                </span>
                <span className="ink-mid">{p.clock} · Place</span>
              </div>
              <h3 className="engrave ink text-[clamp(1.3rem,2.4vw,2rem)] leading-tight">
                {p.name}
              </h3>
              <p className="serif ink mt-2 text-lg italic opacity-80">{p.tagline}</p>
              <p className="ink-mid mt-4 text-[14px] leading-relaxed">{p.body}</p>

              <dl className="mt-6 space-y-2.5 border-t border-[var(--stage-gold)]/15 pt-5">
                {[
                  ["Practice", p.practice],
                  ["Artifact", p.artifact],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-baseline gap-3">
                    <dt className="mono w-16 shrink-0 text-[8px] uppercase text-[var(--stage-gold)]/70">
                      {k}
                    </dt>
                    <dd className="ink text-[13.5px]">{v}</dd>
                  </div>
                ))}
              </dl>

              <button
                type="button"
                data-testid={`tease-enter-${p.key}`}
                onClick={() => enter(p)}
                className="tracked group mt-7 flex items-center gap-2 rounded-full bg-[var(--stage-gold)] px-6 py-3 text-[10px] uppercase text-[#1a1206] transition-all hover:brightness-110"
              >
                Enter {p.practice}
                <ArrowRight
                  size={12}
                  strokeWidth={2}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </button>
            </div>

            <Walk place={p} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default PlaceTease;
